import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import get from 'lodash/get';

import { makeStyles } from 'src/makeStyles';
import { capitalize } from 'lodash';
import { withTranslation } from '../../src/i18n';
import { removeWithTranslationProps } from '../../utils/common';
import { borderRadius, colors, fontSize } from '../../constants/styleConstants';

const useTextFieldStyles = makeStyles()(theme => ({
  field: {
    marginTop: 0,
    marginBottom: 0,
    width: '100%',
  },
  disabledField: {
    backgroundColor: `${colors.background.grayLight} !important`,
    color: `${colors.textGray} !important`,
  },
  inputLabel: {
    color: `${colors.title} !important`,
    letterSpacing: 0.6,
    textAlign: 'left',
    '&.Mui-error': {
      color: `${colors.red} !important`,
    },
  },
  input: {
    padding: '8px 10px !important',
    fontSize: fontSize.textFieldLabel,
    height: 29,
    backgroundColor: colors.white,
    '&:focus': {
      backgroundColor: colors.background.gray,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: fontSize.basic,
    },
  },
  inputBox: {
    width: '100%',
    // paddingRight: 10,
    margin: 0,
    border: `1px solid ${colors.fieldsBorderGray} !important`,
    borderRadius: borderRadius.fields,
    backgroundColor: colors.white,
    '&>input[type=number]': {
      '-moz-appearance': 'textfield',
      '&::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '&::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },
  },
  focusedField: {
    backgroundColor: colors.background.gray,
  },
  underline: {
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
    '&::before': {
      borderBottom: '0px solid transparent !important',
    },
  },
  fieldLabelFocused: {},
  asterisk: {
    color: colors.primary,
    lineHeight: `${fontSize.textFieldLabel}px`,
  },
  error: {
    border: `1px solid ${colors.red} !important`,
    color: colors.textGray,
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
  },
  withMargin: {
    marginTop: `calc(${fontSize.textFieldLabel}px + 3px) !important`,
  },
  shrink: {
    lineHeight: 'calc(1em + 2px) !important',
    fontSize: `${fontSize.smallTitle}px !important`,
    transform: 'translate(0, 1.5px) scale(1) !important',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis',
  },
}));

function FormikTextField({
  t,
  field,
  form: { errors, touched, setFieldValue },
  id,
  inputLabelProps,
  inputProps,
  label,
  required,
  inputClasses,
  className,
  autoCapitalizeOnPaste,
  nativeInputProps,
  ...rest
}) {
  const errorText =
    !get(touched, field.name) ||
    get(errors, field.name, '')
      .split(' ')
      .map(text => t(text))
      .join(' ');
  const hasError = get(touched, field.name) && Boolean(get(errors, field.name));
  const { classes, cx } = useTextFieldStyles();

  const pasteCorrection = event => {
    if (autoCapitalizeOnPaste) {
      const clipBoardValue = event?.clipboardData?.getData('Text') ?? '';
      let newValue = capitalize(clipBoardValue);

      if (event?.target?.value) {
        const element = event.target;
        const currentValue = element.value;

        newValue = currentValue?.slice(0, element?.selectionStart);
        newValue += capitalize(clipBoardValue);
        newValue += currentValue?.slice(element?.selectionEnd);

        // Set the caret position correctly after paste
        const caretPos = element?.selectionStart + clipBoardValue?.length;
        window.requestAnimationFrame(() => {
          element.setSelectionRange(caretPos, caretPos);
        });
      }

      setFieldValue(field?.name, newValue);
      event.preventDefault();
    }
  };

  return (
    <TextField
      id={id}
      margin="normal"
      label={label}
      className={cx(classes.field, className)}
      required={required}
      error={hasError}
      helperText={errorText}
      {...field}
      {...removeWithTranslationProps(rest)}
      InputLabelProps={{
        shrink: true,
        classes: {
          root: classes.inputLabel,
          focused: classes.fieldLabelFocused,
          asterisk: classes.asterisk,
          shrink: classes.shrink,
        },
        ...inputLabelProps,
      }}
      InputProps={{
        classes: {
          root: cx(classes.inputBox, { [classes.withMargin]: Boolean(label) }),
          input: classes.input,
          underline: classes.underline,
          disabled: classes.disabledField,
          error: classes.error,
          focused: classes.focusedField,
          ...inputClasses,
        },
        autoComplete: 'off',
        ...inputProps,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={nativeInputProps}
      onPaste={pasteCorrection}
    />
  );
}

FormikTextField.getInitialProps = async () => ({
  namespacesRequired: ['translation'],
});

FormikTextField.propTypes = {
  t: PropTypes.func.isRequired,
  field: PropTypes.object,
  form: PropTypes.shape({
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    touched: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    setFieldValue: PropTypes.func,
  }),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  label: PropTypes.string,
  required: PropTypes.bool,
  inputClasses: PropTypes.object,
  className: PropTypes.string,
  autoCapitalizeOnPaste: PropTypes.bool,
};

FormikTextField.defaultProps = {
  form: {
    errors: {},
    touched: {},
    setFieldValue: () => {},
  },
  field: {
    name: '',
  },
  autoCapitalizeOnPaste: false,
};

export default withTranslation('translation')(FormikTextField);
